/**
 * Image Upload Service
 * 
 * Handles image uploads to Supabase Storage for market images.
 */

import { getBackendUrl } from '../utils/backendUrl';

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed file types
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

class ImageUploadService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getBackendUrl();
  }

  /**
   * Validate image file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return { 
        valid: false, 
        error: 'Invalid file type. Allowed: JPEG, PNG, GIF, WebP' 
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: 'File too large. Maximum size: 10MB' 
      };
    }

    return { valid: true };
  }

  /**
   * Upload image to backend (which uploads to Supabase Storage)
   * 
   * @param file - Image file to upload
   * @returns Upload result with public URL
   */
  async uploadMarketImage(file: File): Promise<UploadResult> {
    // Validate first
    const validation = this.validateFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${this.baseUrl}/api/upload/market-image`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'Upload failed'
        };
      }

      return {
        success: true,
        url: result.url
      };

    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to upload image'
      };
    }
  }

  /**
   * Generate a unique filename for the image
   */
  generateFilename(originalName: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop() || 'jpg';
    return `market_${timestamp}_${random}.${extension}`;
  }
}

export const imageUploadService = new ImageUploadService();
